import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useLayoutEffect, useMemo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { adsApi } from '@/api/client';
import { extractAccountRows } from '@/api/managerAccounts';
import {
  setActiveMoneyContext,
  type MoneyContext,
} from '@/utils/formatCurrency';
import { parseWorkspaceScope } from '@/api/workspaceScope';

const AccountCurrencyContext = createContext<MoneyContext>({});

function amazonMoneyContext(data: unknown, profileId: string): MoneyContext {
  const row = extractAccountRows(data).find((account) => account.profileId === profileId);
  if (!row) return {};
  return {
    currencyCode: row.currencyCode ?? null,
    countryCode: row.countryCode ?? null,
    accountType: row.accountType ?? null,
  };
}

export function AccountCurrencyProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const scope = parseWorkspaceScope(pathname);

  const amazonQuery = useQuery({
    queryKey: ['advertising-profiles'],
    queryFn: async () => {
      const res = await adsApi.getAdvertisingProfiles();
      if (!res.success) throw new Error('Failed to load accounts');
      return res.data;
    },
    enabled: scope?.platform === 'amazon',
  });

  const value = useMemo<MoneyContext>(() => {
    if (!scope || scope.platform !== 'amazon') return {};
    return amazonMoneyContext(amazonQuery.data, scope.accountId);
  }, [scope, amazonQuery.data]);

  setActiveMoneyContext(value);

  useLayoutEffect(() => {
    setActiveMoneyContext(value);
  }, [value]);

  useLayoutEffect(() => {
    return () => {
      setActiveMoneyContext({});
    };
  }, []);

  return (
    <AccountCurrencyContext.Provider value={value}>{children}</AccountCurrencyContext.Provider>
  );
}

export function useAccountCurrency(): MoneyContext {
  return useContext(AccountCurrencyContext);
}
